import React, {useState} from "react";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  activateCampaign,
  deleteCampaign,
  selectIsLoading,
  selectIsLoadingMore,
  selectIsUpdating
} from "../../redux/campaign/campaignListSlice";
import {ColumnProps} from "rc-table/lib/sugar/Column";
import {Heading4, Paragraph, TextStyles} from "../text";
import Moment from "moment";
import Table from "rc-table";
import {Button} from "../buttons/Button";
import {useModalRouting} from "../../utils/useModalRouting";
import {MessageCampaignModel} from "../../models/messageCampaign";
import {TableHeader} from "../table/TableHeader";
import {TableCell} from "../table/TableCell";
import {ArrowDownIcon} from "../icons";
import {AlertDialog} from "../dialogs/AlertDialog";
import {useRouter} from "next/router";

export interface PromptsTableProps {
  campaigns: MessageCampaignModel[]
  onLoadMoreClicked: () => void
}

const CampaignTableActionButton: React.FC<{ campaign: MessageCampaignModel }> = ({ campaign }) => {
  const isUpdating = useAppSelector(state => selectIsUpdating(state, campaign.id))
  const dispatch = useAppDispatch()
  const { createRoute } = useModalRouting()
  const router = useRouter()

  const [campaignIdToDelete, setCampaignIdToDelete] = useState<string>()
  const [campaignIdToActivate, setCampaignIdToActivate] = useState<string>()

  return <div className="relative">
    <div className="absolute top-0 left-0">
      <Button
        isLoading={isUpdating}
        buttonStyle="secondary"
        trailingIconSizeClass="h-3 w-3"
        trailingicon={<ArrowDownIcon />}>
        <span className="capitalize">Actions</span>
      </Button>
    </div>

    <select
      id="actions"
      name="actions"
      value={'blank'}
      onClick={e => {
        e.stopPropagation()
      }}
      onChange={e => {
        if (!e.target.value) return

        const campaignId = campaign.id
        if (!campaignId) return

        switch (e.target.value) {
          case 'edit':
            router.push(
              createRoute({ name: 'campaign_detail', query: { campaign_id: campaign.id }, returnTo: '/admin/campaigns' }),
              undefined,
              { shallow: true }
            )
            break;
          case 'delete':
            setCampaignIdToDelete(campaign.id)
            break;
          case 'activate':
            setCampaignIdToActivate(campaign.id)
            break;
        }
      }}
      className={`appearance-none opacity-0 transition-colors bg-fg-primary hover:bg-fg-gray-surface-dark border-0 cursor-pointer text-center focus:ring-0 rounded-full px-5 py-2 flex flex-row items-center ${TextStyles.Body}`}
    >
      <option key={'blank'} value={'blank'}/>
      <option key={'edit'} value={'edit'}>Edit</option>
      <option key={'delete'} value={'delete'}>Delete</option>
      { campaign.status !== 'active' && <option key={'activate'} value={'activate'}>Activate</option> }
    </select>

    <AlertDialog
      isOpen={!!campaignIdToDelete}
      message={'Are you sure you want to delete this campaign?'}
      title={'Delete Campaign?'}
      buttons={[
        {
          title: { key: 'common:common_title_delete' },
          onClick: () => {
            campaignIdToDelete && dispatch(deleteCampaign(campaignIdToDelete))
            setCampaignIdToDelete(undefined)
          },
          style: 'destructive'
        },
        {
          title: { key: 'common:common_title_cancel' },
          onClick: () => setCampaignIdToDelete(undefined),
          style: 'cancel'
        }
      ]}
    />

    <AlertDialog
      isOpen={!!campaignIdToActivate}
      message={'Are you sure you want to activate this campaign?'}
      title={'Activate Campaign?'}
      buttons={[
        {
          title: 'Activate',
          onClick: () => {
            campaignIdToActivate && dispatch(activateCampaign(campaignIdToActivate))
            setCampaignIdToActivate(undefined)
          },
          style: 'default'
        },
        {
          title: { key: 'common:common_title_cancel' },
          onClick: () => setCampaignIdToActivate(undefined),
          style: 'cancel'
        }
      ]}
    />
  </div>
}

export const CampaignsTable: React.FC<PromptsTableProps> = ({ campaigns, onLoadMoreClicked }) => {
  const isLoading = useAppSelector(selectIsLoading)
  const isLoadingMore = useAppSelector(selectIsLoadingMore)

  const columns: ColumnProps<MessageCampaignModel>[] = [
    {
      title: <TableHeader isFirst={true}>
        <Heading4 className="text-fg-gray-dark">Name</Heading4>
      </TableHeader>,
      dataIndex: 'name',
      render: (text, record) => (
        <TableCell>
          <Paragraph>{`${record.name}`}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Create On</Heading4>
      </TableHeader>,
      dataIndex: 'created_at',
      render: (text, record) => (
        <TableCell>
          <Paragraph>{Moment(record.createdAt).format('MMM D, YYYY')}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Starts On</Heading4>
      </TableHeader>,
      dataIndex: 'starts_on',
      render: (text, record) => (
        <TableCell>
          <Paragraph>{record.startOn && Moment(record.startOn).format('MMM D, YYYY')}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Ends On</Heading4>
      </TableHeader>,
      dataIndex: 'ends_on',
      render: (text, record) => (
        <TableCell>
          <Paragraph>{record.endOn && Moment(record.endOn).format('MMM D, YYYY')}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Status</Heading4>
      </TableHeader>,
      dataIndex: 'status',
      render: (text, record) => (
        <TableCell>
          <Paragraph className="capitalize">{record.status}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader isLast={true}>

      </TableHeader>,
      dataIndex: 'action',
      render: (text, record) => (
        <TableCell>
          <CampaignTableActionButton campaign={record} />
        </TableCell>
      )
    }
  ]

  return <Table
    className="rounded-lg border border-fg-gray-surface fg-table"
    scroll={{x: true}}
    data={campaigns}
    columns={columns}
    emptyText={() => {
      return <div className="w-full h-16 flex items-center justify-center">
        <Paragraph>No Campaigns</Paragraph>
      </div>
    }}
    footer={() => {
      return <div className="w-full flex items-center justify-center py-8 border-t border-fg-gray-surface">
        <Button
          buttonStyle="secondary"
          isLoading={isLoadingMore}
          onClick={() => {
            onLoadMoreClicked()
          }}
        >
          Load More
        </Button>
      </div>
    }}
  />
}